import { message, Row } from 'antd';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  Collapse,
  CompanyContactsTable,
  Form,
  FormItem,
  FormItemCEP,
  FormItemCNPJ,
  FormItemCPF,
  FormItemDynamicPhone,
  FormItemInput,
  FormItemSelect,
} from '../../../../components/index';
import {
  useCreateCompanyMutation,
  useDeleteCompanyMutation,
  useGetCompaniesQuery,
  useUpdateCompanyMutation,
} from '../../../../services/companyApi';
import { useGetCompanyTypesQuery } from '../../../../services/companyTypeApi';
import { useGetBerthsQuery } from '../../../../services/dockingPlacesApi';
import { CompanyContactType, CompanyType } from '../../../../types';
import { cargoNatureList, companySectorList } from '../../../../utils/lists';
import {
  isNullOrUndefined,
  maskCNPJ,
  removeSpecialCharacters,
} from '../../../../utils/utils';
import { RegisterHeaderForm } from '../registerHeaderForm';
import { useMutationWithErrorControl } from '../../../../hooks/useMutationWithErrorControl';

const { Panel } = Collapse;

export const FormItemsWrapper = styled.div`
  padding: 24px 16px;
`;

type CompanyFormProps = {
  allCompaniesCount: number | undefined;
  onClose: (show: null) => void;
  setQueryPage: (page: number) => void;
  selectedCompany: CompanyType | undefined;
  refetchCompanyData: () => void;
  isEdit: boolean;
};

const STORAGE = 8;
const OPERTATOR = 11;

export function CompanyForm(props: CompanyFormProps) {
  const { allCompaniesCount, onClose, setQueryPage, selectedCompany, isEdit } =
    props;
  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [isStorage, setIsStorage] = useState(false);
  const [isOperator, setIsOperator] = useState(false);
  const [CNPJFormatted, setCNPJFormatted] = useState('');
  const [valideteStatusCNPJ, setValideteStatusCNPJ] = useState('');

  const [formInitialValues, setFormInitialValues] = useState<
    CompanyType | undefined
  >(selectedCompany);

  const [
    createCompany,
    {
      isSuccess: isSuccessCreateCompany,
      isError: isErrorCreateCompany,
      error: errorCreateCompany,
    },
  ] = useCreateCompanyMutation();

  const createCompanyWithControl = useMutationWithErrorControl(createCompany);

  const [
    updateCompany,
    {
      isSuccess: isSuccessUpdateCompany,
      isError: isErrorUpdateCompany,
      error: errorUpdateCompany,
    },
  ] = useUpdateCompanyMutation();

  const updateCompanyWithControl = useMutationWithErrorControl(updateCompany);

  const [
    deleteCompany,
    { isSuccess: isSuccessDeleteCompany, isError: isErrorDeleteCompany },
  ] = useDeleteCompanyMutation();

  const { data: allCompaniesData } = useGetCompaniesQuery({
    page_size: allCompaniesCount,
  });

  const { data: allCompanyTypesData } = useGetCompanyTypesQuery({
    page_size: 100,
  });

  const { data: allBerthsData } = useGetBerthsQuery({ page_size: 100 });

  const { data: operatorBerthsData, isLoading: isLoadingOperatorBerthsData } =
    useGetBerthsQuery(
      {
        page_size: 100,
        operator: selectedCompany?.name,
      },
      {
        skip: !isEdit || !selectedCompany?.name || !isOperator,
        refetchOnMountOrArgChange: true,
      }
    );

  useEffect(() => {
    if (isEdit && selectedCompany) {
      setFormInitialValues(selectedCompany);
      form.setFieldsValue(selectedCompany);
    }
  }, [selectedCompany, isEdit]);

  useEffect(() => {
    if (isEdit && selectedCompany) {
      form.setFieldsValue({
        docking_places: isOperator ? operatorBerthsData?.results : [],
      });
    }
  }, [operatorBerthsData]);

  useEffect(() => {
    if (isEdit) {
      setIsStorage(
        form
          .getFieldValue('company_type')
          .some((companyType: number) => companyType === STORAGE)
      );
      setIsOperator(
        form
          .getFieldValue('company_type')
          .some((companyType: number) => companyType === OPERTATOR)
      );
    }

    if (isSuccessUpdateCompany) {
      message.success('Empresa atualizada');
      setIsLoading(false);
      onClose(null);
    } else if (isErrorUpdateCompany) {
      setIsLoading(false);
      handleApiError(errorUpdateCompany);
    }
  }, [isSuccessUpdateCompany, isErrorUpdateCompany, errorUpdateCompany]);

  useEffect(() => {
    if (isSuccessCreateCompany) {
      message.success('Empresa criada');
      setIsLoading(false);
      onClose(null);
    } else if (isErrorCreateCompany) {
      setIsLoading(false);
      handleApiError(errorCreateCompany);
    }
  }, [isSuccessCreateCompany, isErrorCreateCompany, errorCreateCompany]);

  useEffect(() => {
    if (isSuccessDeleteCompany) {
      message.success('Empresa excluída');
      onClose(null);
    } else if (isErrorDeleteCompany) {
      message.error(
        'Ocorreu um erro ao excluir a Empresa, se o erro persistir entre em contato com o suporte'
      );
    }
  }, [isSuccessDeleteCompany, isErrorDeleteCompany]);

  async function onDeleteCompany() {
    if (selectedCompany && selectedCompany.id) {
      setQueryPage(1);
      await deleteCompany(selectedCompany.id);
      props.refetchCompanyData();
    }
  }
  function isValidCNPJ(value: string) {
    const cnpjNumber = value.replace(/\D/g, '');
    const cnpjAlphanumeric = value.replace(/\W/g, '');

    if (cnpjNumber.length === 14 && isValidateCNPJNumeric(value)) return true;
    if (cnpjAlphanumeric.length === 14 && isValidCnpjAlphanumeric(value))
      return true;
    return false;
  }

  function isValidateCNPJNumeric(value: string) {
    if (!value) return false;

    const cnpj = value.replace(/\D/g, '');
    if (cnpj.length !== 14) return false;

    const calcCheckDigit = (base: string, weights: number[]) => {
      const sum = base
        .split('')
        .reduce((acc, digit, index) => acc + Number(digit) * weights[index], 0);

      const remainder = sum % 11;
      return remainder < 2 ? 0 : 11 - remainder;
    };

    const base12 = cnpj.substring(0, 12);
    const digit1 = Number(cnpj.charAt(12));
    const digit2 = Number(cnpj.charAt(13));

    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    const check1 = calcCheckDigit(base12, weights1);
    const check2 = calcCheckDigit(base12 + check1, weights2);

    return check1 === digit1 && check2 === digit2;
  }

  function isValidCnpjAlphanumeric(value: string): boolean {
    const weightDV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const valueBase = '0'.charCodeAt(0);
    if (!value) return false;

    const cnpj = value.replace(/\W/g, '');
    if (cnpj.length !== 14) return false;

    let sunDV1 = 0;
    let sumDV2 = 0;
    for (let i = 0; i < 12; i++) {
      const asciiDig = cnpj.charCodeAt(i) - valueBase;
      sunDV1 += asciiDig * weightDV[i + 1];
      sumDV2 += asciiDig * weightDV[i];
    }
    const dv1 = sunDV1 % 11 < 2 ? 0 : 11 - (sunDV1 % 11);
    sumDV2 += dv1 * weightDV[12];
    const dv2 = sumDV2 % 11 < 2 ? 0 : 11 - (sumDV2 % 11);
    const digit1 = Number(cnpj.charAt(12));
    const digit2 = Number(cnpj.charAt(13));

    return dv1 === digit1 && dv2 === digit2;
  }

  async function handleFormSubmit(values: any) {
    setIsLoading(true);

    if (values.cnpj) {
      if (!isValidCNPJ(values.cnpj)) {
        message.error('CNPJ inválido!');
        setValideteStatusCNPJ('error');
        setIsLoading(false);
        return;
      }
    }

    if (!validateCompany(values)) {
      setIsLoading(false);
      return;
    }

    const company: CompanyType = {
      ...values,
      phone_number: removeSpecialCharacters(values.phone_number),
      cnpj: removeSpecialCharacters(values.cnpj),
      trade_name: values.trade_name?.trim() || undefined,
      tin: values.tin?.trim() || undefined,
      responsible_cpf: values.responsible_cpf
        ? removeSpecialCharacters(values.responsible_cpf)
        : undefined,
      responsible_phone: values.responsible_phone
        ? removeSpecialCharacters(values.responsible_phone)
        : undefined,
      responsible_email: values.responsible_email?.trim() || undefined,
      responsible_sector: values.responsible_sector || undefined,
      contacts: values.contacts?.map((contact: CompanyContactType) => ({
        ...contact,
        name: contact.name?.trim(),
        email: contact.email?.trim(),
      })),
      address: {
        ...values.address,
        postal_code: removeSpecialCharacters(values.address?.postal_code),
      },
      docking_places: isOperator
        ? (values.docking_places || []).map((place: any) =>
            typeof place === 'string' ? place : place.tag
          )
        : undefined,
      antaq_storage_code: isStorage
        ? values.antaq_storage_code?.trim()
        : undefined,
    };

    if (isEdit) {
      await updateCompanyWithControl(company, { suppressErrorAlert: true });
    } else {
      await createCompanyWithControl(company, { suppressErrorAlert: true });
    }
    setIsLoading(false);
  }

  function validateCompany(companySaved: CompanyType) {
    let existError = false;
    const existCompanyWithSameName = allCompaniesData?.results.some(
      (company) =>
        company.id !== companySaved.id &&
        company.name.toLowerCase() === companySaved.name.toLowerCase()
    );

    if (existCompanyWithSameName) {
      message.error('Já existe uma empresa cadastrada com o Nome informado');
      existError = true;
    }

    const existCompanyWithSameTradeName =
      !isNullOrUndefined(companySaved.trade_name) &&
      allCompaniesData?.results.some(
        (company) =>
          company.id !== companySaved.id &&
          !isNullOrUndefined(company.trade_name) &&
          company.trade_name?.toLowerCase() ===
            companySaved.trade_name?.toLowerCase()
      );

    if (existCompanyWithSameTradeName) {
      message.error(
        'Já existe uma empresa cadastrada com o Nome Fantasia informado'
      );
      existError = true;
    }

    const existCompanyWithSameCnpj = allCompaniesData?.results.some(
      (company) =>
        company.id !== companySaved.id &&
        !isNullOrUndefined(company.cnpj) &&
        company.cnpj?.toLowerCase() === companySaved.cnpj?.toLowerCase()
    );

    if (existCompanyWithSameCnpj) {
      message.error('Já existe uma empresa cadastrada com o CNPJ informado');
      existError = true;
    }

    if (
      isNullOrUndefined(companySaved.tin) &&
      (isNullOrUndefined(companySaved.cnpj) ||
        companySaved.cnpj === '__.___.___/____-__')
    ) {
      message.error(
        'É necessário informar o CNPJ ou o TIN para cadastrar uma empresa'
      );
      existError = true;
    }

    if (
      !isNullOrUndefined(companySaved.tin) &&
      !isNullOrUndefined(companySaved.cnpj) &&
      companySaved.cnpj !== '__.___.___/____-__'
    ) {
      message.error('Não é possível informar o CNPJ e o TIN para uma empresa');
      existError = true;
    }

    const existCompanyWithSameTin = allCompaniesData?.results.some(
      (company) =>
        company.id !== companySaved.id &&
        !isNullOrUndefined(company.tin) &&
        company.tin?.toLowerCase() === companySaved.tin?.toLowerCase()
    );

    if (existCompanyWithSameTin) {
      message.error('Já existe uma empresa cadastrada com o TIN informado');
      existError = true;
    }

    const existCompanyWithSameEmail = allCompaniesData?.results.some(
      (company) =>
        company.id !== companySaved.id &&
        !isNullOrUndefined(company.email) &&
        company.email?.toLowerCase() === companySaved.email?.toLowerCase()
    );

    if (existCompanyWithSameEmail) {
      message.error('Já existe uma empresa cadastrada com o Email informado');
      existError = true;
    }

    if (existError) {
      setIsLoading(false);
      return false;
    }
    return true;
  }

  function extractValidationErrors(errorMessage: string): string[] {
    const errors: string[] = [];

    const fieldErrorRegex = /'(\w+)':\s*\[ErrorDetail\(string='([^']+)'/g;

    let match = fieldErrorRegex.exec(errorMessage);
    while (match !== null) {
      const field = match[1];
      const errorMsg = match[2];

      const fieldLabels: { [key: string]: string } = {
        responsible_cpf: 'CPF do Responsável',
        responsible_name: 'Nome do Responsável',
        responsible_phone: 'Telefone do Responsável',
        responsible_email: 'E-mail do Responsável',
        responsible_sector: 'Função / Setor do Responsável',
        contacts: 'Responsáveis por Setor',
        name: 'Nome',
        trade_name: 'Nome Fantasia',
        cnpj: 'CNPJ',
        email: 'E-mail',
        phone_number: 'Telefone',
        antaq_storage_code: 'Código ANTAQ de Armazém',
      };

      const fieldLabel = fieldLabels[field] || field;
      errors.push(`${fieldLabel}: ${errorMsg}`);

      match = fieldErrorRegex.exec(errorMessage);
    }

    return errors;
  }

  function handleApiError(error: unknown) {
    if (!error) {
      message.error(
        'Ocorreu um erro ao salvar a empresa, se o erro persistir entre em contato com o suporte'
      );
      return;
    }

    try {
      const errorData = error as { data?: { message?: string } };
      const errorMessage = errorData?.data?.message;

      if (errorMessage && typeof errorMessage === 'string') {
        const validationErrors = extractValidationErrors(errorMessage);
        if (validationErrors.length > 0) {
          validationErrors.forEach((err) => message.error(err));
          return;
        }

        if (errorMessage.includes('ErrorDetail')) {
          message.error(
            'Ocorreu um erro de validação. Verifique os dados informados.'
          );
          return;
        }
      }
    } catch (e) {
      // Se ocorrer um erro ao processar a mensagem, cairá aqui
    }

    message.error(
      'Ocorreu um erro ao salvar a empresa, se o erro persistir entre em contato com o suporte'
    );
  }

  function onSelectCompanyType() {
    setIsStorage(false);
    setIsOperator(false);
    if (form.getFieldValue('company_type').includes(OPERTATOR)) {
      setIsOperator(true);
    }
    if (form.getFieldValue('company_type').includes(STORAGE)) {
      setIsStorage(true);
    }
  }

  return (
    <Form
      form={form}
      name="company"
      layout="vertical"
      onFinish={handleFormSubmit}
      initialValues={formInitialValues}
    >
      <RegisterHeaderForm
        onClose={onClose}
        isLoading={isLoading}
        formName="company"
        allowDelete={!!selectedCompany?.id}
        onDelete={onDeleteCompany}
      >
        <div className="title">
          {isEdit ? 'EDITAR EMPRESA' : 'NOVA EMPRESA'}
        </div>
      </RegisterHeaderForm>
      <FormItemsWrapper>
        <FormItem noStyle name="id" />
        <Row>
          <FormItemInput label="Nome" name="name" maxLength={100} required />
        </Row>
        <Row>
          <FormItemInput
            label="Nome Fantasia"
            name="trade_name"
            maxLength={100}
          />
        </Row>
        <Row>
          <FormItemInput label="Atividade" name="activity" maxLength={70} />
        </Row>
        <Row gutter={20}>
          <FormItemInput
            label="E-mail"
            name="email"
            type="email"
            placeholder="empresa@email.com"
            maxLength={50}
          />
        </Row>
        <Row>
          <FormItemDynamicPhone />
        </Row>
        {/* <Row>
          <FormItemCNPJ readOnly={isEdit} />
        </Row> */}
        <Row>
          <FormItemInput
            label="CNPJ"
            name="cnpj"
            maxLength={50}
            value={CNPJFormatted}
            validateStatus={
              valideteStatusCNPJ
                ? valideteStatusCNPJ === 'success'
                  ? ''
                  : 'error'
                : undefined
            }
            onChange={(value: any) => {
              let cnpj = value.target.value;
              setValideteStatusCNPJ('');
              if (!cnpj) {
                return;
              }
              if (cnpj.length < 14) {
                return;
              }
              if (cnpj.length > 18) {
                cnpj = cnpj.slice(-18);
              }
              form.setFieldsValue({
                cnpj: maskCNPJ(cnpj),
              });
            }}
          />
        </Row>
        <Row>
          <FormItemInput
            label="Código Internacional"
            name="tin"
            maxLength={50}
          />
        </Row>
        <Row>
          <FormItemSelect
            name="company_type"
            label="Tipo da Empresa"
            dataList={allCompanyTypesData?.results}
            allowClear
            required
            mode="multiple"
            maxSelectOptions={5}
            onSelect={() => onSelectCompanyType()}
            onClear={() => onSelectCompanyType()}
          />
        </Row>
        {isOperator && (
          <Row>
            <FormItemSelect
              name="docking_places"
              label="Berços Atendidos"
              dataList={allBerthsData?.results}
              allowClear
              disabled={isLoadingOperatorBerthsData}
              required={isOperator}
              mode="multiple"
              maxSelectOptions={allBerthsData ? allBerthsData.count : 1}
            />
          </Row>
        )}
        {isStorage && (
          <>
            <Row>
              <FormItemSelect
                name="nature_of_cargo"
                label="Natureza da Carga"
                dataList={cargoNatureList}
                allowClear
                required={isStorage}
              />
            </Row>
            <Row>
              <FormItemInput
                label="Código ANTAQ de Armazém"
                name="antaq_storage_code"
                maxLength={4}
                placeholder="0000"
                rules={[
                  {
                    required: isStorage,
                    message:
                      'Código ANTAQ obrigatório para empresas do tipo Armazém',
                  },
                  {
                    pattern: /^\d{4}$/,
                    message: 'O código deve conter exatamente 4 dígitos',
                  },
                ]}
              />
            </Row>
          </>
        )}
      </FormItemsWrapper>
      <Collapse
        defaultActiveKey={['address_data', 'responsible_data', 'contacts_data']}
      >
        <Panel
          forceRender
          header="Responsável Principal"
          key="responsible_data"
        >
          <Row>
            <FormItemInput
              label="Nome do Responsável"
              name="responsible_name"
              maxLength={100}
            />
          </Row>
          <Row gutter={20}>
            <FormItemCPF
              label="CPF do Responsável"
              name="responsible_cpf"
              colSpan={12}
            />
            <FormItemInput
              label="Telefone do Responsável"
              name="responsible_phone"
              maxLength={50}
              colSpan={12}
            />
          </Row>
          <Row gutter={20}>
            <FormItemInput
              label="E-mail do Responsável"
              name="responsible_email"
              type="email"
              placeholder="responsavel@empresa.com.br"
              maxLength={100}
              colSpan={12}
            />
            <FormItemSelect
              label="Função / Setor do Responsável"
              name="responsible_sector"
              dataList={companySectorList}
              allowClear
              colSpan={12}
            />
          </Row>
        </Panel>
        <Panel forceRender header="Responsáveis por Setor" key="contacts_data">
          <CompanyContactsTable />
        </Panel>
        <Panel forceRender header="Endereço" key="address_data">
          <Row>
            <FormItemInput
              label="Endereço"
              name={['address', 'address']}
              maxLength={70}
            />
          </Row>
          <Row>
            <FormItemInput
              label="Complemento"
              name={['address', 'complement']}
              maxLength={70}
            />
          </Row>
          <Row gutter={20}>
            <FormItemInput
              label="Bairro"
              name={['address', 'neighborhood']}
              maxLength={70}
              colSpan={12}
            />
            <FormItemCEP
              name={['address', 'postal_code']}
              required={false}
              colSpan={12}
            />
          </Row>
          <Row gutter={20}>
            <FormItemInput
              label="Estado"
              name={['address', 'state']}
              colSpan={12}
              maxLength={70}
            />
            <FormItemInput
              label="Cidade"
              name={['address', 'city']}
              maxLength={70}
              colSpan={12}
            />
          </Row>
          <Row gutter={20}>
            <FormItemInput
              label="País"
              name={['address', 'country']}
              maxLength={70}
            />
          </Row>
        </Panel>
      </Collapse>
    </Form>
  );
}
